import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Sparkles,
  Users,
  Compass,
  Mail,
  ArrowRight,
  ArrowLeft,
  Clock,
  Send,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import ContentPage from "./ContentPage";
import "./Careers.css";

export default function Careers() {
  const { isRTL, language } = useLanguage();

  useEffect(() => {
    document.title = isRTL
      ? "الوظائف وفرص العمل | تيري لاند للمنسوجات الفاخرة"
      : "Careers & Opportunities | Terry Land Luxury Textiles";
  }, [isRTL, language]);

  const values = [
    {
      icon: <Sparkles size={24} />,
      title: isRTL ? "شغف الجودة والحِرفة" : "Uncompromising Craftsmanship",
      desc: isRTL
        ? "نفتخر بأدق تفاصيل منتجاتنا من اختيار خامات القطن والمايكروفايبر إلى التغليف الملكي الفاخر."
        : "We take immense pride in every textile detail, from fiber sourcing to our signature luxury presentation.",
    },
    {
      icon: <Users size={24} />,
      title: isRTL ? "فريق ملهم ومتعاون" : "Collaborative & Empowering Culture",
      desc: isRTL
        ? "بيئة عمل إيجابية تحتفي بالإبداع الفردي والعمل الجماعي وتقدير مساهمات كل فرد في الفريق."
        : "A positive work atmosphere celebrating individual creativity, team spirit, and continuous professional growth.",
    },
    {
      icon: <Compass size={24} />,
      title: isRTL ? "الابتكار والتطوير الدائم" : "Continuous Innovation",
      desc: isRTL
        ? "نطور باستمرار حلول النسيج الذكية وتجارب التجارة الإلكترونية العصرية لنلبي تطلعات عملائنا."
        : "Pioneering smart textile solutions and modern e-commerce experiences that redefine home comfort.",
    },
  ];

  return (
    <ContentPage
      title={isRTL ? "فرص العمل والمسار المهني" : "Careers at Terry Land"}
      eyebrow={isRTL ? "انضم إلى عائلة تيري لاند" : "Join Our Atelier"}
    >
      {/* Lead Paragraph */}
      <p className="lead careers-lead">
        {isRTL
          ? "نبني في تيري لاند فريقاً شغوفاً يؤمن بأعلى معايير جودة المنتجات، الحِرفية الدقيقة، وصناعة تجربة استثنائية لعملائنا في كل لمسة. إذا كنت تبحث عن بيئة عمل ملهمة تُقدر التميز، يسعدنا انضمامك إلينا."
          : "At Terry Land, we are building a passionate team committed to exceptional craftsmanship, uncompromising textile quality, and elevated customer journeys. If you are driven by excellence and creativity, we would love to welcome you."}
      </p>

      {/* ── Section: Why Work With Us ── */}
      <section className="careers-section">
        <h2>
          {isRTL ? "لماذا تختار العمل معنا؟" : "Why Build Your Career With Us?"}
        </h2>
        <p className="careers-section-sub">
          {isRTL
            ? "نؤمن بأن نجاحنا يبدأ من فريق العمل؛ لذا نوفر بيئة محفزة تساعدك على إطلاق طاقاتك والإبداع:"
            : "We believe our greatest asset is our people. Here is what you can expect when you join our journey:"}
        </p>

        <div className="careers-values-grid">
          {values.map((v, idx) => (
            <div key={idx} className="careers-value-card">
              <div className="careers-value-icon">{v.icon}</div>
              <h3>{v.title}</h3>
              <p>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Section: Open Roles Status ── */}
      <section className="careers-section">
        <h2>
          {isRTL ? "الوظائف المتاحة حالياً" : "Current Opportunities"}
        </h2>

        <div className="careers-status-card">
          <div className="careers-status-icon">
            <Clock size={28} />
          </div>
          <div className="careers-status-content">
            <h3>
              {isRTL
                ? "لا توجد شواغر معلنة في الوقت الحالي"
                : "No Open Roles Listed at the Moment"}
            </h3>
            <p>
              {isRTL
                ? "فريقنا مكتمل حالياً، ولكننا نرحب دائماً بالمهارات الاستثنائية والشغوفة بالانضمام إلينا في مجالات: تصميم المنسوجات، التجارة الإلكترونية، خدمة وتجربة العملاء، والتسويق الرقمي."
                : "Our current roles are filled, but we are always eager to connect with talented individuals in textile design, e-commerce operations, concierge customer care, and digital marketing."}
            </p>
          </div>
        </div>
      </section>

      {/* ── Section: Spontaneous Application CTA ── */}
      <div className="careers-apply-box">
        <div className="careers-apply-badge">
          <Briefcase size={14} />
          <span>{isRTL ? "طلب توظيف عام" : "General Application"}</span>
        </div>

        <h3 className="careers-apply-title">
          {isRTL
            ? "هل ترى في نفسك إضافة مميزة لفريق تيري لاند؟"
            : "Do you see yourself as a great addition to Terry Land?"}
        </h3>

        <p className="careers-apply-desc">
          {isRTL
            ? "أرسل سيرتك الذاتية ونبذة عن خبراتك وطموحاتك إلى بريد التوظيف، وسيقوم فريق الموارد البشرية بالتواصل معك فور توفر فرصة تلائم مؤهلاتك."
            : "Send your CV and a brief introduction highlighting your passion and experience to our talent team. We will gladly reach out as soon as a fitting position opens."}
        </p>

        <div className="careers-apply-actions">
          <a
            href="mailto:careers@terryland.net?subject=Job%20Application%20-%20Terry%20Land"
            className="btn-primary"
          >
            <Mail size={16} />
            <span>{isRTL ? "أرسل سيرتك الذاتية" : "Send Your Resume"}</span>
            {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
          </a>

          <Link to="/contact" className="btn-outline">
            <Send size={15} />
            <span>{isRTL ? "تواصل معنا" : "Contact Concierge"}</span>
          </Link>
        </div>
      </div>
    </ContentPage>
  );
}
